import type { ReactNode } from 'react';
import { cx } from '../ui';
import styles from './listing.module.css';

/**
 * The controls over a grid card's frame — a remove, a Watch later clock, or both stacked
 * (2026-08-03).
 *
 * One component because there were three copies of it: the feed card's, the history card's and the
 * playlist entry's, near-identical and drifting apart. The playlist copy had never grown the
 * `any-hover: none` arm, which on a touch screen left its remove and its Watch later with no way to
 * appear at all; `listing.module.css` holds the recipe and what else the three disagreed about.
 *
 * A **sibling** of the thumbnail link rather than a child of it: a `<button>` nested in an `<a>` is
 * invalid HTML that browsers resolve by dropping one of the two. And it comes **after** the card's
 * text in the DOM even though it is drawn over the picture — absolute positioning makes the two
 * independent, and reading order is what decides the tab order, so a keyboard reaches the video's
 * title before the corner buttons that act on it.
 */
export function CardActions({
  backdrop = 'scrim',
  covered = false,
  children,
}: {
  /**
   * `scrim` over a thumbnail (the feed and history grids, whose buttons are `media`), `surface`
   * where the buttons are ghosts that expect a card surface under them (the playlist entries).
   */
  readonly backdrop?: 'scrim' | 'surface';
  /**
   * Something else has the picture, so this stack stands down — a card asking what a press on it
   * meant (`queue/QueueCardAsk.tsx`). It takes the whole thumbnail, corner included, and leaving
   * three buttons underneath it would put controls in the tab order that nobody can see or reach.
   */
  readonly covered?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <span
      className={cx(
        styles.cardActions,
        backdrop === 'surface' && styles.cardActionsSurface,
        covered && styles.cardActionsCovered,
      )}
    >
      {children}
    </span>
  );
}
